import axios from 'axios';

import { STORAGE_KEYS } from '../../hooks/Auth/useAuth';
import { RefreshReq, RefreshRes } from '../../types/Auth/types';
import { AUTH_EVENTS, authEvents } from '../../utils/authEvents';
import { StorageService } from '../../utils/storage';
import {
  buildTrackingHttpActionLabel,
  normalizeTrackingApiEndpoint,
  recordTrackingApiFailure,
  recordTrackingApiSuccess,
  recordTrackingSuccessfulAction,
} from '../../utils/trackingDiagnostics';
import {
  getApiBaseUrl,
  LOGIN,
  RECOVERYPASS,
  REFRESH,
  REGISTER,
  TRACKING_SESSION_SUMMARY,
  USER_TRACKING,
} from './constants';

const instance = axios.create({
  baseURL: getApiBaseUrl(),
});

export const refreshApiBaseUrl = (): void => {
  instance.defaults.baseURL = getApiBaseUrl();
};

type TrackedRequestConfig = {
  _retry?: boolean;
  trackingMetadata?: {
    startedAt: number;
    action: string;
    endpoint: string;
    method: string;
  };
  headers?: {
    Authorization?: string;
  };
  method?: string;
  url?: string;
};

const shouldTrackRequestDiagnostics = (endpoint: string): boolean => {
  const normalizedEndpoint = normalizeTrackingApiEndpoint(endpoint);

  return normalizedEndpoint !== normalizeTrackingApiEndpoint(USER_TRACKING)
    && normalizedEndpoint !== normalizeTrackingApiEndpoint(TRACKING_SESSION_SUMMARY)
    && normalizedEndpoint !== normalizeTrackingApiEndpoint(REFRESH);
};

const AUTH_ENDPOINTS_WITHOUT_ACCESS_TOKEN = new Set(
  [LOGIN, REGISTER, RECOVERYPASS, REFRESH].map((endpoint) =>
    normalizeTrackingApiEndpoint(endpoint),
  ),
);

const shouldAttachAccessToken = (endpoint: string | undefined): boolean =>
  !AUTH_ENDPOINTS_WITHOUT_ACCESS_TOKEN.has(normalizeTrackingApiEndpoint(endpoint));

/**
 * Verifica si un token JWT está expirado
 * @param token - Token JWT a verificar
 * @param marginMs - Margen de tiempo en ms antes de la expiración (default: 60s)
 */
const isTokenExpired = (token: string, marginMs = 60000): boolean => {
  try {
    const { exp } = JSON.parse(atob(token.split('.')[1]));
    return Date.now() >= exp * 1000 - marginMs;
  } catch {
    return true;
  }
};

/**
 * Realiza el refresh de tokens
 */
export const refresh = async ({ refreshToken }: RefreshReq): Promise<{ ok: boolean; data?: RefreshRes }> => {
  try {
    const { data } = await instance.post<RefreshRes>(REFRESH, { refresh_token: refreshToken });
    return { ok: true, data };
  } catch {
    return { ok: false };
  }
};

// Request interceptor - Agrega token a las peticiones
instance.interceptors.request.use(async (config) => {
  const trackedConfig = config as TrackedRequestConfig;
  const token = await StorageService.getSecure(STORAGE_KEYS.token);
  if (token && shouldAttachAccessToken(trackedConfig.url)) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  const endpoint = normalizeTrackingApiEndpoint(trackedConfig.url);
  const method = (trackedConfig.method ?? 'get').toUpperCase();

  if (shouldTrackRequestDiagnostics(endpoint)) {
    trackedConfig.trackingMetadata = {
      startedAt: Date.now(),
      action: buildTrackingHttpActionLabel(method, endpoint),
      endpoint,
      method,
    };
  }

  return config;
});

// Singleton para el refresh - evita race conditions
let refreshPromise: Promise<{ ok: boolean; data?: RefreshRes }> | null = null;

/**
 * Ejecuta el refresh de tokens usando singleton pattern.
 * Si ya hay un refresh en progreso, espera a que termine.
 */
const executeRefresh = async (): Promise<{ ok: boolean; data?: RefreshRes }> => {
  if (refreshPromise) {
    return refreshPromise;
  }

  const refreshToken = await StorageService.getSecure(STORAGE_KEYS.refreshToken);

  if (!refreshToken) {
    return { ok: false };
  }

  // Validar si el refresh token también expiró
  if (isTokenExpired(refreshToken)) {
    return { ok: false };
  }

  refreshPromise = refresh({ refreshToken }).finally(() => {
    refreshPromise = null;
  });

  return refreshPromise;
};

/**
 * Guarda los nuevos tokens en el almacenamiento seguro
 */
const saveTokens = async (tokens: RefreshRes): Promise<void> => {
  await Promise.all([
    StorageService.setSecure(STORAGE_KEYS.token, tokens.token),
    StorageService.setSecure(STORAGE_KEYS.refreshToken, tokens.refreshToken),
  ]);
};

/**
 * Maneja la expiración de sesión emitiendo un evento
 * en lugar de redirigir directamente
 */
const handleSessionExpired = (): void => {
  authEvents.emit(AUTH_EVENTS.SESSION_EXPIRED);
};

// Response interceptor - Maneja refresh automático de tokens
instance.interceptors.response.use(
  (res) => {
    const trackedConfig = res.config as TrackedRequestConfig;
    const metadata = trackedConfig.trackingMetadata;

    if (metadata?.action) {
      recordTrackingSuccessfulAction(metadata.action);
      recordTrackingApiSuccess({
        endpoint: metadata.endpoint,
        method: metadata.method,
        status: res.status,
        durationMs: Date.now() - metadata.startedAt,
      });
    }

    return res;
  },
  async (error) => {
    const { status, data } = error?.response ?? {};
    const originalRequest = (error?.config ?? {}) as TrackedRequestConfig;
    const metadata = originalRequest.trackingMetadata;

    if (metadata && shouldTrackRequestDiagnostics(metadata.endpoint)) {
      recordTrackingApiFailure({
        endpoint: metadata.endpoint,
        method: metadata.method,
        status: status ?? null,
        durationMs: Date.now() - metadata.startedAt,
        errorMessage: data?.message ?? error?.message ?? null,
        action: metadata.action,
      });
    }

    // Si no es 401, propagar el error
    if (status !== 401) return Promise.reject(error);

    // Las rutas de auth deben devolver el error real al formulario.
    if (!shouldAttachAccessToken(originalRequest.url)) return Promise.reject(error);

    // Si el mensaje no es de JWT expirado, la sesión es inválida
    if (data?.message !== 'jwt expired') {
      handleSessionExpired();
      return Promise.reject(error);
    }

    // Evitar loops infinitos
    if (originalRequest._retry) {
      handleSessionExpired();
      return Promise.reject(error);
    }

    originalRequest._retry = true;

    try {
      const { ok, data: tokens } = await executeRefresh();

      if (!ok || !tokens) {
        handleSessionExpired();
        return Promise.reject(error);
      }

      // Guardar nuevos tokens
      await saveTokens(tokens);

      // Emitir evento de refresh exitoso
      authEvents.emit(AUTH_EVENTS.SESSION_REFRESHED);

      // Reintentar la petición original con el nuevo token
      originalRequest.headers.Authorization = `Bearer ${tokens.token}`;
      return axios(originalRequest);
    } catch (refreshError) {
      handleSessionExpired();
      return Promise.reject(refreshError);
    }
  },
);

export default instance;
